import { useCallback, useEffect, useState } from 'react';
import { api } from '../api';
import { useToast } from '../ui/Toast';
import { ConfirmDialog } from '../ui/ConfirmDialog';
import { Badge, EmptyState, LoadingPanel, PageHeader } from '../ui/bits';

/**
 * Sales/Proposals (DESIGN §11.1): plan configurator → generated branded
 * proposal PDF; proposal → won tracking with the onboarding hand-off.
 */

interface PlanOption {
  key: string;
  name: string;
  level: number;
  monthlyPriceCents: number;
  setupPriceCents: number;
}
interface PackOption {
  packKey: string;
  packName: string;
  plans: PlanOption[];
}
interface AddonOption {
  name: string;
  monthly_price_cents: number;
  verticals: string[];
  levels?: number[];
}
interface Proposal {
  id: string;
  packKey: string;
  planKey: string;
  prospect: { businessName: string; contactName?: string };
  pricing: { monthlyTotalCents: number; setupTotalCents: number };
  status: string;
  pdfKey: string | null;
  createdAt: string;
}

const money = (cents: number) => `$${(cents / 100).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;

export function Proposals() {
  const [options, setOptions] = useState<{ packs: PackOption[]; addons: AddonOption[] } | null>(null);
  const [rows, setRows] = useState<Proposal[]>([]);
  const [packKey, setPackKey] = useState('');
  const [planKey, setPlanKey] = useState('');
  const [addonQty, setAddonQty] = useState<Record<string, number>>({});
  const [businessName, setBusinessName] = useState('');
  const [contactName, setContactName] = useState('');
  const [notes, setNotes] = useState('');
  const [wonNext, setWonNext] = useState<string | null>(null);
  const [confirmAction, setConfirmAction] = useState<{ row: Proposal; status: 'won' | 'lost' } | null>(null);
  const toast = useToast();

  const load = useCallback(async () => {
    const [opts, list] = await Promise.all([
      api.get<{ packs: PackOption[]; addons: AddonOption[] }>('/api/admin/proposal-options'),
      api.get<Proposal[]>('/api/admin/proposals'),
    ]);
    setOptions(opts);
    setRows(list);
  }, []);
  useEffect(() => {
    load().catch(console.error);
  }, [load]);

  // poll while something is generating
  useEffect(() => {
    if (!rows.some((r) => r.status === 'generating')) return;
    const t = setInterval(() => void load().catch(console.error), 4000);
    return () => clearInterval(t);
  }, [rows, load]);

  if (!options) return <LoadingPanel />;
  const pack = options.packs.find((p) => p.packKey === packKey);
  const plan = pack?.plans.find((p) => p.key === planKey);
  const eligibleAddons = options.addons.filter(
    (a) => pack && a.verticals.includes(pack.packKey) && (!a.levels || (plan && a.levels.includes(plan.level)))
  );
  const addons = Object.entries(addonQty)
    .filter(([name, qty]) => qty > 0 && eligibleAddons.some((a) => a.name === name))
    .map(([name, quantity]) => ({ name, quantity }));
  const monthlyTotal =
    (plan?.monthlyPriceCents ?? 0) +
    addons.reduce((sum, a) => sum + (options.addons.find((o) => o.name === a.name)?.monthly_price_cents ?? 0) * a.quantity, 0);

  const generate = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await api.post('/api/admin/proposals', {
        packKey,
        planKey,
        addons,
        prospect: { businessName, contactName: contactName || undefined, notes: notes || undefined },
      });
      toast.success('Generating — the PDF appears below in a few seconds.');
      setBusinessName('');
      setNotes('');
      void load();
    } catch {
      toast.error('Generation failed to start — check the configuration.');
    }
  };

  const setStatus = async (row: Proposal, status: string) => {
    try {
      const updated = await api.post<{ next?: string }>(`/api/admin/proposals/${row.id}/status`, { status });
      if (status === 'won' && updated.next) setWonNext(updated.next);
      else toast.success(`Marked ${status}.`);
      void load();
    } catch {
      toast.error(`Could not mark ${status}.`);
    }
  };

  return (
    <div className="page-stack">
      <PageHeader title="Proposals" sub="Configure, generate, and track sales proposals" />
      <div className="panel">
        <h2>New proposal</h2>
        <form onSubmit={generate} className="stack" style={{ gap: 10, maxWidth: 640 }}>
          <div className="row">
            <select required value={packKey} aria-label="Vertical" onChange={(e) => { setPackKey(e.target.value); setPlanKey(''); setAddonQty({}); }}>
              <option value="">Vertical…</option>
              {options.packs.map((p) => (
                <option key={p.packKey} value={p.packKey}>{p.packName}</option>
              ))}
            </select>
            <select required value={planKey} aria-label="Plan level" onChange={(e) => setPlanKey(e.target.value)} disabled={!pack}>
              <option value="">Level…</option>
              {pack?.plans.map((p) => (
                <option key={p.key} value={p.key}>
                  L{p.level} — {p.name} ({money(p.monthlyPriceCents)}/mo)
                </option>
              ))}
            </select>
          </div>
          {plan && eligibleAddons.length > 0 && (
            <div className="row" style={{ gap: 16 }}>
              {eligibleAddons.map((a) => (
                <label key={a.name} className="row-baseline" style={{ gap: 6 }}>
                  {a.name} ({money(a.monthly_price_cents)}/mo)
                  <input
                    type="number"
                    min={0}
                    max={20}
                    value={addonQty[a.name] ?? 0}
                    onChange={(e) => setAddonQty({ ...addonQty, [a.name]: Number(e.target.value) })}
                    style={{ width: 56 }}
                  />
                </label>
              ))}
            </div>
          )}
          <div className="row">
            <input required placeholder="Business name" aria-label="Business name" value={businessName} onChange={(e) => setBusinessName(e.target.value)} style={{ flex: 2, minWidth: 200 }} />
            <input placeholder="Contact (optional)" aria-label="Contact" value={contactName} onChange={(e) => setContactName(e.target.value)} style={{ flex: 1, minWidth: 140 }} />
          </div>
          <textarea
            placeholder="Notes from the sales conversation — the proposal's summary is personalized from these"
            aria-label="Sales notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
          />
          <div className="row-baseline" style={{ gap: 12 }}>
            <button className="primary" type="submit" disabled={!plan || !businessName}>
              Generate proposal
            </button>
            {plan && (
              <span className="muted">
                {money(monthlyTotal)}/mo + {money(plan.setupPriceCents)} setup
              </span>
            )}
          </div>
        </form>
        {wonNext && (
          <p className="callout callout--ok row-baseline" style={{ marginTop: 10 }}>
            <span>
              Won 🎉 — onboard with: <span className="mono">{wonNext}</span>
            </span>
            <button type="button" className="btn-sm end" onClick={() => setWonNext(null)}>
              Dismiss
            </button>
          </p>
        )}
      </div>

      <div className="panel">
        <h3>Proposals</h3>
        {rows.length === 0 && <EmptyState title="No proposals yet" hint="Configure one above to generate the first PDF." />}
        {rows.length > 0 && (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Prospect</th>
                  <th>Program</th>
                  <th>Monthly</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id}>
                    <td><strong>{r.prospect.businessName}</strong></td>
                    <td className="muted small">{r.packKey} · {r.planKey}</td>
                    <td>{money(r.pricing.monthlyTotalCents)}/mo</td>
                    <td><Badge state={r.status} /></td>
                    <td className="muted small">{new Date(r.createdAt).toLocaleDateString()}</td>
                    <td>
                      <div className="row" style={{ gap: 6, flexWrap: 'nowrap' }}>
                        {r.pdfKey && (
                          <a href={`/api/admin/proposals/${r.id}/pdf`} target="_blank" rel="noreferrer">PDF</a>
                        )}
                        {r.status === 'draft' && <button type="button" className="btn-sm" onClick={() => void setStatus(r, 'sent')}>Mark sent</button>}
                        {(r.status === 'draft' || r.status === 'sent') && (
                          <>
                            <button type="button" className="btn-sm primary" onClick={() => setConfirmAction({ row: r, status: 'won' })}>Won</button>
                            <button type="button" className="btn-sm" onClick={() => setConfirmAction({ row: r, status: 'lost' })}>Lost</button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <ConfirmDialog
        open={confirmAction !== null}
        danger={confirmAction?.status === 'lost'}
        title={confirmAction?.status === 'won' ? 'Mark as won?' : 'Mark as lost?'}
        body={
          confirmAction?.status === 'won'
            ? `${confirmAction.row.prospect.businessName} moves to won and you'll get the onboarding command.`
            : `This closes the proposal for ${confirmAction?.row.prospect.businessName ?? ''}.`
        }
        confirmLabel={confirmAction?.status === 'won' ? 'Mark won' : 'Mark lost'}
        onConfirm={() => {
          if (confirmAction) void setStatus(confirmAction.row, confirmAction.status);
        }}
        onClose={() => setConfirmAction(null)}
      />
    </div>
  );
}
